import React, { FC, forwardRef, useEffect, useState } from 'react';
import clsx from 'clsx';
import useScreenWidth from '../../../store/useScreenWidth';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { styled, css } from '@mui/system';
import { useNavigate } from 'react-router-dom';
import { Modal as BaseModal } from '@mui/base/Modal';
import { useUserStore } from '../../../store/useUserStore';
import { useDarkModeStore } from '../../../store/useDarkModeStore';
import { useLanguageStore } from '../../../store/useLanguageStore';
import { GroupInterface } from '../../../domain/helpers/NavMenuItem';
import { navBarItems } from '../../../components/NavMenu/NavMenuItems';
import { darkModeBackgroud, darkModeBackgroudInputs } from '../../../utils/constants';

interface SearchTopBarModalProps {
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const SearchTopBarModal: FC<SearchTopBarModalProps> = ({ open, setOpen }) => {
    const isSmallScreen = useScreenWidth();
    const navigate = useNavigate();
    const { language } = useLanguageStore();
    const { user } = useUserStore();
    const { darkMode } = useDarkModeStore();
    const isAdminRole = user?.["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"] === 'Admin';

    const [lists, setLists] = useState<GroupInterface[]>();
    const [searchTerm, setSearchTerm] = useState<string>('');

    useEffect(() => {
        setLists(navBarItems);
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.toLowerCase();
        setSearchTerm(value);

        if (value) {
            const filteredLists = navBarItems.map(list => ({
                ...list,
                items: list.items.filter(item => {
                    const matchesItemName = language[item.name].toLowerCase().includes(value);

                    const matchesSubMenu = item.subMenu?.some(subItem =>
                        language[subItem.name].toLowerCase().includes(value)
                    );

                    const matchesSubMenuSettings = item.subMenuSettings?.some(setting =>
                        setting.subMenuItemSettings.some(subItemSetting =>
                            language[subItemSetting.name].toLowerCase().includes(value)
                        )
                    );

                    return matchesItemName || matchesSubMenu || matchesSubMenuSettings;
                }),
            })).filter(list => list.items.length > 0);

            setLists(filteredLists);
        } else {
            setLists(navBarItems);
        }
    };


    const handleChangeRoute = (route: string) => {
        navigate(route);
        setOpen(false);
    }

    return <Modal
        open={open}
        onClose={() => setOpen(false)}
        slots={{ backdrop: StyledBackdrop }}
    >
        <ModalContent sx={{ width: isSmallScreen ? 300 : 500, minHeight: '600px', backgroundColor: darkMode ? darkModeBackgroud : 'white' }}>
            <div className='d-flex flex-column'>
                <div className='d-flex flex-row p-2 pt-3 pb-0'>
                    <div className="input-group flex-nowrap">
                        <span className="input-group-text border-0" id="addon-wrapping" style={{ background: darkMode ? darkModeBackgroudInputs : 'white' }}>
                            <SearchOutlinedIcon style={{ color: darkMode ? 'white' : '#4D5F71' }} />
                        </span>
                        <input
                            type="text"
                            autoFocus
                            className="form-control border-0 input-focus-none ps-0"
                            style={{ color: darkMode ? 'white' : '#4D5F71', fontSize: '16px', background: darkMode ? darkModeBackgroudInputs : 'white' }}
                            value={searchTerm}
                            onChange={handleChange}
                        />
                        <span
                            className="input-group-text border-0"
                            role='button'
                            style={{ color: darkMode ? 'white' : '#32475C61', background: darkMode ? darkModeBackgroudInputs : 'white' }}
                            onClick={() => setOpen(false)}
                        >
                            [esc] <CloseOutlinedIcon sx={{ width: '16px', height: '16px', marginLeft: '13px', color: darkMode ? 'white' : '#909BAF' }} />
                        </span>
                    </div>
                </div>
                <hr style={{ color: 'grey' }} />
                <div className="container">
                    <div className={`p-4 pt-0`} style={{ maxHeight: '500px', overflowY: 'auto' }}>
                        {
                            lists?.map(list => <div key={list.groupName} className='col'>
                                <div className="mt-2 ms-2">
                                    <ul className="list-unstyled">
                                        <p
                                            className='text-center'
                                            style={{ fontSize: '12px', color: darkMode ? 'white' : 'rgba(50, 71, 92, 0.38)', marginBottom: '10px' }}
                                        >
                                            {list.groupName !== 'none'
                                                && !(list.groupName === 'SETTINGS' && user?.["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"] === 'Employee')
                                                && (language[list.groupName.toUpperCase()] || list.groupName)}
                                        </p>
                                        {
                                            list.items?.filter(i => isAdminRole ? i : i.role === 'Employee').map(item =>
                                                item.subMenu ?
                                                    item.subMenu.map(i =>
                                                        <li
                                                            key={i.id}
                                                            onClick={() => handleChangeRoute(i.route)}
                                                            role="button"
                                                            className={`list-input-search-modal ${darkMode && 'list-input-search-modal-darkMode'}`}
                                                        >
                                                            {i.icon}
                                                            <p className='mb-0 pt-1 ms-2'>{language[i.name] || i.name}</p>
                                                        </li>
                                                    )
                                                    : item.subMenuSettings
                                                        ? item.subMenuSettings.map(subMenu =>
                                                            subMenu?.subMenuItemSettings?.map(subMenuItem =>
                                                                <li
                                                                    key={subMenuItem.id}
                                                                    onClick={() => handleChangeRoute(subMenuItem.route)}
                                                                    role="button"
                                                                    className={`list-input-search-modal ${darkMode && 'list-input-search-modal-darkMode'}`}
                                                                >
                                                                    {subMenuItem.icon}
                                                                    <p className='mb-0 pt-1 ms-2'>{language[subMenuItem.name] || subMenuItem.name}</p>
                                                                </li>
                                                            )
                                                        )
                                                        : <li
                                                            key={item.name}
                                                            role="button"
                                                            onClick={() => item.route ? handleChangeRoute(item.route) : null}
                                                            className={`list-input-search-modal ${darkMode && 'list-input-search-modal-darkMode'}`}
                                                        >
                                                            {item.icon}
                                                            <p className='mb-0 pt-1 ms-2'>{language[item.name] || item.name}</p>
                                                        </li>
                                            )
                                        }
                                    </ul>
                                </div>
                            </div>)
                        }
                    </div>
                </div>
            </div>
        </ModalContent>
    </Modal>
}

const grey = {
    50: '#F3F6F9',
    100: '#E5EAF2',
    200: '#DAE2ED',
    300: '#C7D0DD',
    400: '#B0B8C4',
    500: '#9DA8B7',
    600: '#6B7A90',
    700: '#434D5B',
    800: '#303740',
    900: '#1C2025',
};

export const Modal = styled(BaseModal)`
  position: fixed;
  z-index: 1300;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const Backdrop = forwardRef<
    HTMLDivElement,
    { open?: boolean; className: string }
>((props, ref) => {
    const { open, className, ...other } = props;
    return (
        <div
            className={clsx({ 'base-Backdrop-open': open }, className)}
            ref={ref}
            {...other}
        />
    );
});

export const ModalContent = styled('div')(
    ({ theme }) => css`
      font-family: 'IBM Plex Sans', sans-serif;
      font-weight: 500;
      text-align: start;
      position: relative;
      display: flex;
      flex-direction: column;
      gap: 8px;
      overflow: hidden;
      background-color: ${theme.palette.mode === 'dark' ? grey[900] : '#fff'};
      border-radius: 8px;
      box-shadow: 0 4px 12px;
        ${theme.palette.mode === 'dark' ? 'rgb(0 0 0 / 0.5)' : 'rgb(0 0 0 / 0.2)'};
      color: ${theme.palette.mode === 'dark' ? grey[50] : grey[900]};
  
      & .modal-title {
        margin: 0;
        line-height: 1.5rem;
        margin-bottom: 8px;
      }
  
      & .modal-description {
        margin: 0;
        line-height: 1.5rem;
        font-weight: 400;
        color: ${theme.palette.mode === 'dark' ? grey[400] : grey[800]};
        margin-bottom: 4px;
      }
    `,
);

export const StyledBackdrop = styled(Backdrop)`
  z-index: -1;
  position: fixed;
  inset: 0;
  background-color: rgb(0 0 0 / 0.5);
  -webkit-tap-highlight-color: transparent;
`;
export default SearchTopBarModal;